import type { ExtractedLeadV2 } from "@/types/lead";

export type SalesDeskLeadContact = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
};

export type SalesDeskQuoteContact = {
  email: string | null;
  phone: string | null;
};

export type SalesDeskStaffContact = {
  emails: string[];
  phones: string[];
};

export type SalesDeskQuoteMatch =
  | { kind: "attached"; leadId: string }
  | { kind: "inbox"; reason: "staff" | "missing" | "none" | "many" };

export function salesDeskEmailKey(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

export function salesDeskPhoneKey(value: string | null | undefined): string {
  const digits = (value ?? "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  if (digits.length === 10) return digits;
  return "";
}

export function salesDeskLeadContactFromRow(row: { id: string; data: unknown }): SalesDeskLeadContact | null {
  if (!row.data || typeof row.data !== "object") return null;
  const data = row.data as { name?: unknown; extracted?: ExtractedLeadV2 | null; isTest?: unknown };
  if (data.isTest === true) return null;
  const extracted = data.extracted;
  const name = typeof data.name === "string" && data.name.trim() ? data.name.trim() : "Unnamed lead";
  return {
    id: row.id,
    name,
    email: extracted?.email ?? null,
    phone: extracted?.phone ?? null,
  };
}

function isStaffContact(quote: { email: string; phone: string }, staff: SalesDeskStaffContact): boolean {
  const staffEmails = new Set(staff.emails.map(salesDeskEmailKey).filter(Boolean));
  const staffPhones = new Set(staff.phones.map(salesDeskPhoneKey).filter(Boolean));
  return (quote.email !== "" && staffEmails.has(quote.email)) || (quote.phone !== "" && staffPhones.has(quote.phone));
}

export function matchSalesDeskQuoteToLead(
  quote: SalesDeskQuoteContact,
  leads: SalesDeskLeadContact[],
  staff: SalesDeskStaffContact,
): SalesDeskQuoteMatch {
  const email = salesDeskEmailKey(quote.email);
  const phone = salesDeskPhoneKey(quote.phone);
  if (!email || !phone) return { kind: "inbox", reason: "missing" };
  if (isStaffContact({ email, phone }, staff)) return { kind: "inbox", reason: "staff" };
  const hits = leads.filter(
    (lead) => salesDeskEmailKey(lead.email) === email && salesDeskPhoneKey(lead.phone) === phone,
  );
  if (hits.length === 1) return { kind: "attached", leadId: hits[0].id };
  if (hits.length === 0) return { kind: "inbox", reason: "none" };
  return { kind: "inbox", reason: "many" };
}
